import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from '@mui/material';

interface PrivacyPolicyDialogProps {
  open: boolean;
  onClose: () => void;
}

const PrivacyPolicyDialog: React.FC<PrivacyPolicyDialogProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2">
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Effective Date: November 6, 2025
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto', pr: 1 }}>
          <Typography variant="body2" paragraph sx={{ fontWeight: 'bold' }}>
            Your privacy is very important to us. Before we get to the details, here's a summary of our key privacy practices.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Privacy Summary
          </Typography>
          
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we sell your personal information for monetary value?</strong> No
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we share your personal information with third parties that are not service providers?</strong> Yes, with your consent
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we use sensitive categories of data, like health information?</strong> Yes, with your consent
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we provide additional privacy protections for minors (users under 18)?</strong> Yes
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we delete your personal information when you request account deletion?</strong> Yes, unless it is necessary to provide the Waka Running services or required by law
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Can you control who sees your activity and content?</strong> Yes
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Can you download and delete your personal information?</strong> Yes
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we track your device location while you are not using our app?</strong> No
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              <strong>Do we track your device location to provide Waka Running services to you?</strong> Yes, with your consent
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Introduction
          </Typography>
          
          <Typography variant="body2" paragraph>
            Waka Running's privacy policy (the "Privacy Policy") explains the information we collect, how we use and share it, 
            how to manage your privacy controls, and your rights in connection with our websites and the related mobile applications 
            and services (collectively, the "Services"). Please also read our Terms of Service, which set out the terms governing the Services.
          </Typography>

          <Typography variant="body2" paragraph>
            If you have any questions about our privacy practices, this Privacy Policy, or would like to contact us, you can do so 
            by the email or addresses below.
          </Typography>

          <Box sx={{ pl: 2, mb: 2, mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Contact Information:
            </Typography>
            <Typography variant="body2">
              Waka Running
              <br />
              3826 Dudley Dr.
              <br />
              Mishawaka, IN 46544
              <br />
              Phone: <Link href="tel:574-993-2331">574-993-2331</Link>
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Information Waka Running Collects
          </Typography>
          
          <Typography variant="body2" paragraph>
            Waka Running collects information about you, including information that directly or indirectly identifies you, if you 
            or other Waka Running users choose to share it with Waka Running. The sources from which we collect this personal 
            information fall into three categories: information you provide, information we collect automatically, and information 
            we collect from other sources.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Account, Profile, and User Activity
          </Typography>
          <Typography variant="body2" paragraph>
            We collect account information such as your name, email address, date of birth, gender, username and password that 
            helps secure and provide you with access to our Services. Profile, activity and use information is collected about you 
            when you engage in activities such as choosing to upload a photo, post, or activity (including date, time and geo-location 
            information as well as your speed and pace), join a team or challenge, view others' activities, or otherwise use the Services.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Location Information
          </Typography>
          <Typography variant="body2" paragraph>
            We collect and process location information when you sign up for and use the Services. We do not track your device location 
            while you are not using the Services. In order for most of our core features (e.g., activity tracking, routes) to function, 
            you must grant us permissions in your device's privacy controls to track your device location while you use the Services. 
            If you would like to stop the device location tracking, you may do so at any time by adjusting your device settings.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Content You Share
          </Typography>
          <Typography variant="body2" paragraph>
            We gather information from photos, videos, posts, comments, messages, and other content you share on the Services, including 
            when you participate in team events or create routes.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Health Information
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running may collect or infer health information, such as sleep scores, hydration levels, and nutrition data that you 
            provide. Certain health information may be inferred from sources such as activity data or other measurements. Where health 
            information can be used to uniquely identify you, it is considered personal information, and sensitive personal information 
            under certain laws.
          </Typography>
          <Typography variant="body2" paragraph>
            Before you can upload health information to Waka Running, you must give your explicit consent to the processing of that 
            health information by Waka Running. You can withdraw your consent to Waka Running processing your health information 
            at any time.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Third-Party Accounts
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running allows you to sign up and log in to the Services using accounts you create with third-party products and services, 
            such as Google or Apple (collectively, "Third-Party Accounts"). If you access the Services through Third-Party Accounts, we will 
            collect information you have agreed to make available such as your name, email address, profile information and preferences.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Technical Information, Cookies, and Log Files
          </Typography>
          <Typography variant="body2" paragraph>
            We collect information from your browser, computer, or mobile device, which provides us with technical information when you 
            access or use the Services. This technical information includes device and network information, cookies, log files and analytics 
            information. The Services use log files. The information stored in those files includes IP addresses, browser type, internet 
            service provider (ISP), referring/exit pages, platform type, date/time stamp, and number of clicks.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            How Waka Running Uses Information
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running uses the information we collect and receive as described below.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            To provide the Services
          </Typography>
          <Typography variant="body2" paragraph>
            We use the information we collect and receive to provide the Services, including providing you with the ability to: record your 
            activities and analyze your performance; interact with other users; manage your training and goals; explore new places to go; 
            visualize your activities; and share insights with the Waka Running community.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            To customize your experience
          </Typography>
          <Typography variant="body2" paragraph>
            We use the information we collect and receive to customize your experience. For example, we may suggest challenges, routes, or 
            teams on Waka Running that may interest you, users that you may want to follow, or new features that you may want to try.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            To protect you and the Services
          </Typography>
          <Typography variant="body2" paragraph>
            We use the information we collect and receive to protect users, enforce our Terms of Service and Acceptable Use Policy, and 
            promote safety. For example, to help us find and remove content that violates our terms, such as hate speech or spam, as well 
            as suspend or terminate accounts that share such content.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            To improve our Services
          </Typography>
          <Typography variant="body2" paragraph>
            We use the information we collect and receive, including feedback you provide, to conduct research, and to analyze, develop, 
            troubleshoot, increase functionality, and otherwise improve the Services.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            To communicate with you
          </Typography>
          <Typography variant="body2" paragraph>
            We may also use the information we collect and receive to market and promote the Services, activities and events on Waka Running, 
            including using email and push notifications, in accordance with your preferences.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            How Information is Shared
          </Typography>
          <Typography variant="body2" paragraph>
            We do not sell personal information or aggregated data for monetary value. We may share personal information in accordance with 
            your preferences, as needed to run our business and provide the Services, and where required for legal purposes.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Service Providers
          </Typography>
          <Typography variant="body2" paragraph>
            We may share your information with third parties who provide services to Waka Running such as supporting, improving, promoting 
            and securing the Services, processing payments, or fulfilling orders. These service providers only have access to the information 
            necessary to perform these limited functions on our behalf and are required to protect and secure your information.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Publicly Available Information
          </Typography>
          <Typography variant="body2" paragraph>
            When you join the Waka Running community, certain information may be viewable by other Waka Running users, and if your 
            profile visibility is set to "Everyone," this information is also viewable by the public. We provide a variety of privacy controls 
            to manage the sharing of your information and encourage our users to adjust their controls to align with their desired experience.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Legal Requirements
          </Typography>
          <Typography variant="body2" paragraph>
            We may preserve and share your information with third parties, including law enforcement, public or governmental agencies, or private 
            litigants, if we determine that such disclosure is compelled by or reasonably necessary to comply with the law, including to respond 
            to court orders, warrants, subpoenas, or other legal or regulatory process, or otherwise permitted by law.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Managing Your Privacy Settings
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running offers several features and settings to help you manage your privacy and share your activities. You can control 
            who sees your profile and activities through your privacy controls. You can also adjust notification and email preferences, update 
            account information, and delete your account at any time.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Deleting Information and Accounts
          </Typography>
          <Typography variant="body2" paragraph>
            You can delete your account at any time. After you make an account deletion request, we permanently and irreversibly delete your 
            personal information from our systems, including backups. Once deleted, your data, including your account, activities and place 
            on leaderboards cannot be reinstated. Following your deletion of your account, it may take up to 45 days to delete your personal 
            information and system logs from our systems. Additionally, we may retain information where deletion requests are made to comply 
            with the law and take other actions permitted by law.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Children's Privacy
          </Typography>
          <Typography variant="body2" paragraph>
            Our services are not intended for, nor directed to, children under 13 years of age. We use technical measures to help prevent 
            children under 13 years of age from creating an account and therefore do not knowingly collect personal information from anybody 
            under 13 years of age. If you are under 13 years of age, do not use the Services.
          </Typography>
          <Typography variant="body2" paragraph>
            Moreover, we provide additional privacy and safety protections for users under 18 years of age. For example, when users under 18 
            years of age create an account, their profile and activities are set by default to be viewable by followers only.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            How We Protect Information
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running implements measures to ensure that your information is treated securely and consistently with the terms in this 
            Privacy Policy. We maintain administrative, technical, and physical safeguards appropriate to the sensitivity of your information 
            and designed to protect against unauthorized use, disclosure, or access to personal information.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Your Legal Rights
          </Typography>
          <Typography variant="body2" paragraph>
            Privacy laws provide certain rights to users in certain jurisdictions. You have the right to know, and request access to, the 
            personal information we collect, use, share, or otherwise process about you. You have the right to rectify, restrict, or delete 
            personal information. You can access much of your information by logging into your account. If you require additional access or 
            have questions, please contact us using the contact information provided above.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Retention of Information
          </Typography>
          <Typography variant="body2" paragraph>
            We retain information as long as it is necessary to provide the Services subject to our legal obligations to further retain such 
            information. Information associated with your account will generally be kept until it is no longer necessary to provide the Services 
            or until your account is deleted or becomes inactive. Following your deletion of your account, it may take up to 45 days to fully 
            delete your personal information and system logs from our systems.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Privacy Policy Updates
          </Typography>
          <Typography variant="body2" paragraph>
            Waka Running reserves the right to modify this Privacy Policy at any time. If we make changes to this privacy statement we will 
            post the revised privacy statement and its effective date on this website. If we make changes we deem to be material, we will provide 
            prominent, advance notice. If you object to any changes to this Privacy Policy, you should stop using the Services and delete your account.
          </Typography>

          <Typography variant="body2" paragraph sx={{ mt: 3, fontStyle: 'italic' }}>
            By creating an account or using the Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyPolicyDialog;

